import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { TTypographyProps } from '@rbx/ui';
import processor, {
  TCompileTree,
  generateMarkdownProcessor,
} from '@modules/markdownProcessor/markdownProcessor';
import { MarkdownInlineCodeProps } from './MarkdownInlineCode';
import { MarkdownLinkProps } from './MarkdownLink';
import { ListItemProps } from './MarkdownListItem';

export interface StandardMarkdownProps {
  paragraphProps?: TTypographyProps;
  inlineCodeProps?: MarkdownInlineCodeProps;
  linkProps?: MarkdownLinkProps;
  listItemProps?: ListItemProps; // Used for li text content
  tableProps?: {
    tableStyle?: React.CSSProperties;
    tableRowStyle?: Record<string, unknown>;
    tableCellStyle?: React.CSSProperties;
    tableHeaderCellStyle?: React.CSSProperties;
  };
  children?: string | TCompileTree | null;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore-error children type
const StandardMarkdown = ({
  paragraphProps,
  inlineCodeProps,
  linkProps,
  listItemProps,
  tableProps,
  children,
}: StandardMarkdownProps) => {
  const [content, setContent] = useState<ReactElement | null>(null);

  const markdownProcessor = useMemo(
    () =>
      paragraphProps || inlineCodeProps || linkProps || listItemProps || tableProps
        ? generateMarkdownProcessor({
            paragraphProps,
            inlineCodeProps,
            linkProps,
            listItemProps,
            tableProps,
          })
        : processor,
    [paragraphProps, inlineCodeProps, linkProps, listItemProps, tableProps],
  );

  const processMarkdown = useCallback(
    async (markdown: string) => {
      const { result } = await markdownProcessor.process(markdown);
      setContent(result as ReactElement);
    },
    [markdownProcessor],
  );

  useEffect(() => {
    if (typeof children === 'string' && children !== '') {
      processMarkdown(children);
    } else if (!children) {
      setContent(null);
    }
  }, [children, processMarkdown]);

  if (children && typeof children !== 'string') {
    return markdownProcessor.stringify(children);
  }

  return content;
};

export default StandardMarkdown;
